import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  HostListener,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import type {
  ChatSessionRename,
  ChatSessionScope,
  ChatSessionSort,
  ChatSessionSummary,
} from '@bizzdesign/ai-assistant/data-access';
import type { UUID } from '@bizzdesign/bizzdesign-sdk/uuid';
import { SharedI18nModule } from '@bizzdesign/shared/i18n';
import { getHighlightedTextParts } from '@bizzdesign/shared/pipes';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports -- Angular constructorParametersDownlevelTransform needs this as a runtime value
import { TranslocoService } from '@jsverse/transloco';
import { InputTextModule } from 'primeng/inputtext';
import type { MenuItem } from 'primeng/api';
import { MenuModule } from 'primeng/menu';
import { debounceTime, distinctUntilChanged, filter, Subject } from 'rxjs';

const SEARCH_DEBOUNCE_MS = 250;
const SESSION_MENU_HEIGHT_PX = 80;

function isHtmlInputElement(
  target: EventTarget | null,
): target is HTMLInputElement {
  return target instanceof HTMLInputElement;
}

function isHtmlButtonElement(
  target: EventTarget | null,
): target is HTMLButtonElement {
  return target instanceof HTMLButtonElement;
}

@Component({
  selector: 'bd-ai-chat-history',
  standalone: true,
  imports: [SharedI18nModule, InputTextModule, MenuModule],
  templateUrl: './ai-chat-history.component.html',
  styleUrl: './ai-chat-history.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
/**
 * Renders persisted assistant chat sessions and lets the user reopen or delete them.
 */
export class AiChatHistoryComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly hostElement = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly searchTerms$ = new Subject<string | null>();
  sessions = input<ChatSessionSummary[]>([]);
  status = input<'idle' | 'loading' | 'loaded' | 'error'>('idle');
  hasMore = input(false);
  activeSessionId = input<UUID | null>(null);
  loadingSessionId = input<UUID | null>(null);
  deletingSessionIds = input<UUID[]>([]);
  renamingSessionId = input<UUID | null>(null);
  disabled = input(false);
  searchTerm = input('');
  sort = input<ChatSessionSort>('lastUsed');
  scope = input<ChatSessionScope>('all');
  hasFrameContext = input(false);
  sessionSelected = output<UUID>();
  deleteSelectedSessions = output<UUID[]>();
  renameSession = output<ChatSessionRename>();
  loadMoreRequested = output<void>();
  searchRequested = output<string>();
  sortChanged = output<ChatSessionSort>();
  scopeChanged = output<ChatSessionScope>();
  protected readonly searchTermDraft = signal('');
  protected readonly searchVisible = signal(false);
  protected readonly filterMenuVisible = signal(false);
  protected readonly sortMenuVisible = signal(false);
  protected readonly menuSessionId = signal<UUID | null>(null);
  protected readonly sessionMenuOpensUpward = signal(false);
  protected readonly editingSessionId = signal<UUID | null>(null);
  protected readonly renameDraft = signal('');
  protected readonly renameInvalid = computed(
    () => this.renameDraft().trim().length === 0,
  );
  private readonly sessionMenuTriggerId = signal<UUID | null>(null);
  private readonly renameInput =
    viewChild<ElementRef<HTMLInputElement>>('renameInput');
  private readonly renameEditor =
    viewChild<ElementRef<HTMLElement>>('renameEditor');
  private readonly originalSessionName = signal('');
  private readonly optimisticDeletingSessionIds = signal<UUID[]>([]);
  protected readonly activeLang = toSignal(this.transloco.langChanges$, {
    initialValue: this.transloco.getActiveLang(),
  });
  protected readonly actionsDisabled = computed(
    () =>
      this.disabled() ||
      this.loadingSessionId() !== null ||
      this.renamingSessionId() !== null ||
      this.pendingDeletionSessionIds().length > 0,
  );
  protected readonly pendingDeletionSessionIds = computed(() =>
    Array.from(
      new Set([
        ...this.deletingSessionIds(),
        ...this.optimisticDeletingSessionIds(),
      ]),
    ),
  );
  protected readonly controlsDisabled = this.actionsDisabled;
  protected readonly loadingMore = computed(
    () => this.status() === 'loading' && this.sessions().length > 0,
  );
  protected readonly hasSearchTermDraft = computed(
    () => this.searchTermDraft().trim().length > 0,
  );
  protected readonly filterMenuItems = computed<MenuItem[]>(() => {
    this.activeLang();
    return [
      this.createMenuItem(
        'history.scopeOptions.all',
        this.scope() === 'all',
        () => this.selectScope('all'),
      ),
      this.createMenuItem(
        'history.scopeOptions.contextual',
        this.scope() === 'contextual',
        () => this.selectScope('contextual'),
      ),
    ];
  });
  protected readonly sortMenuItems = computed<MenuItem[]>(() => {
    this.activeLang();
    return [
      this.createMenuItem(
        'history.sortOptions.lastUsed',
        this.sort() === 'lastUsed',
        () => this.selectSort('lastUsed'),
      ),
      this.createMenuItem(
        'history.sortOptions.name',
        this.sort() === 'name',
        () => this.selectSort('name'),
      ),
    ];
  });

  constructor(private readonly transloco: TranslocoService) {
    let previousSearchTerm = this.searchTerm();
    let deletingSessionIds = this.deletingSessionIds();
    effect(() => {
      const searchTerm = this.searchTerm();
      if (searchTerm === previousSearchTerm) {
        return;
      }
      previousSearchTerm = searchTerm;
      if (this.searchVisible() || searchTerm.length > 0) {
        this.searchTermDraft.set(searchTerm);
        this.searchVisible.set(true);
      }
    });
    effect(() => {
      const currentDeletingSessionIds = this.deletingSessionIds();
      if (
        deletingSessionIds.length > 0 &&
        currentDeletingSessionIds.length === 0
      ) {
        this.optimisticDeletingSessionIds.set([]);
      }
      deletingSessionIds = currentDeletingSessionIds;
    });
    effect(() => this.renameInput()?.nativeElement.focus());
    this.searchTerms$
      .pipe(
        debounceTime(SEARCH_DEBOUNCE_MS),
        distinctUntilChanged(), // Avoid repeat requests for the same trimmed search term.
        filter(
          (searchTerm): searchTerm is string =>
            searchTerm !== null && this.searchVisible(),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((searchTerm) => this.searchRequested.emit(searchTerm));
  }

  protected selectSession(deliverableId: UUID): void {
    if (this.actionsDisabled()) {
      return;
    }
    this.menuSessionId.set(null);
    this.sessionSelected.emit(deliverableId);
  }

  @HostListener('document:click', ['$event'])
  protected handleDocumentClick(event: MouseEvent): void {
    const target = event.target;
    if (this.editingSessionId() !== null) {
      if (
        target instanceof Node &&
        !this.renameEditor()?.nativeElement.contains(target)
      ) {
        this.submitRename();
      }
      return;
    }
    this.closeSessionMenu();
  }

  protected closeSessionMenu(): void {
    this.menuSessionId.set(null);
    this.sessionMenuOpensUpward.set(false);
    this.sessionMenuTriggerId.set(null);
  }

  @HostListener('document:keydown.escape')
  protected closeSessionMenuOnEscape(): void {
    const sessionMenuTriggerId = this.sessionMenuTriggerId();
    if (this.editingSessionId() !== null) {
      this.cancelRename();
      this.focusSessionMenuTrigger(sessionMenuTriggerId, true);
      return;
    }
    this.closeSessionMenu();
    this.focusSessionMenuTrigger(sessionMenuTriggerId);
  }

  protected toggleSessionMenu(deliverableId: UUID, event: Event): void {
    event.stopPropagation();
    if (this.actionsDisabled() || !isHtmlButtonElement(event.currentTarget)) {
      return;
    }
    if (this.menuSessionId() === deliverableId) {
      this.closeSessionMenu();
      return;
    }
    this.menuSessionId.set(deliverableId);
    this.sessionMenuOpensUpward.set(
      this.shouldOpenSessionMenuUpward(event.currentTarget),
    );
    this.sessionMenuTriggerId.set(deliverableId);
  }

  private shouldOpenSessionMenuUpward(menuTrigger: HTMLButtonElement): boolean {
    const historyList = menuTrigger.closest<HTMLElement>(
      '.bd-chat-history-list',
    );
    if (historyList === null) {
      return false;
    }
    return (
      menuTrigger.getBoundingClientRect().bottom + SESSION_MENU_HEIGHT_PX >
      historyList.getBoundingClientRect().bottom
    );
  }

  protected deleteSession(deliverableId: UUID, event: Event): void {
    event.stopPropagation();
    if (this.actionsDisabled()) {
      return;
    }
    this.menuSessionId.set(null);
    this.optimisticDeletingSessionIds.set([deliverableId]);
    this.deleteSelectedSessions.emit([deliverableId]);
  }

  protected startRenaming(session: ChatSessionSummary, event: Event): void {
    event.stopPropagation();
    if (this.actionsDisabled()) {
      return;
    }
    this.menuSessionId.set(null);
    this.originalSessionName.set(session.name);
    this.renameDraft.set(session.name);
    this.editingSessionId.set(session.deliverableId);
  }

  protected onRenameInput(event: Event): void {
    if (isHtmlInputElement(event.target)) {
      this.renameDraft.set(event.target.value);
    }
  }

  protected clearRename(): void {
    this.renameDraft.set('');
    this.renameInput()?.nativeElement.focus();
  }

  protected submitRename(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    const deliverableId = this.editingSessionId();
    const name = this.renameDraft().trim();
    if (deliverableId === null) {
      return;
    }
    if (name.length === 0) {
      this.renameInput()?.nativeElement.focus();
      return;
    }
    if (name !== this.originalSessionName()) {
      this.renameSession.emit({ deliverableId, name });
    }
    this.cancelRename();
  }

  private cancelRename(): void {
    this.editingSessionId.set(null);
    this.renameDraft.set('');
    this.originalSessionName.set('');
    this.sessionMenuTriggerId.set(null);
  }

  private focusSessionMenuTrigger(
    sessionId: UUID | null,
    afterRender = false,
  ): void {
    if (sessionId === null) {
      return;
    }
    const focus = () => {
      this.hostElement.nativeElement
        .querySelector<HTMLButtonElement>(`[data-session-id="${sessionId}"]`)
        ?.focus();
    };
    if (afterRender) {
      requestAnimationFrame(focus);
      return;
    }
    focus();
  }

  protected loadMore(): void {
    if (!this.hasMore() || this.actionsDisabled() || this.loadingMore()) {
      return;
    }
    this.loadMoreRequested.emit();
  }

  protected toggleSearch(): void {
    if (this.controlsDisabled()) {
      return;
    }
    const searchVisible = !this.searchVisible();
    this.searchVisible.set(searchVisible);
    if (!searchVisible) {
      this.searchTerms$.next(null);
    }
    this.searchRequested.emit(
      searchVisible ? this.searchTermDraft().trim() : '',
    );
  }

  protected onSearchTermInput(event: Event): void {
    if (!isHtmlInputElement(event.target)) {
      return;
    }
    this.searchTermDraft.set(event.target.value);
    this.searchTerms$.next(event.target.value.trim());
  }

  protected clearSearch(): void {
    if (this.controlsDisabled() || !this.hasSearchTermDraft()) {
      return;
    }
    this.searchTermDraft.set('');
    this.searchTerms$.next('');
  }

  protected sessionNameParts(
    sessionName: string,
  ): { readonly text: string; readonly highlighted: boolean }[] {
    const searchTerm = this.searchVisible()
      ? this.searchTermDraft().trim()
      : '';
    if (searchTerm.length === 0) {
      return [{ text: sessionName, highlighted: false }];
    }
    return getHighlightedTextParts(sessionName, searchTerm, true);
  }

  protected selectSort(sort: ChatSessionSort): void {
    if (this.controlsDisabled() || sort === this.sort()) {
      return;
    }
    this.sortChanged.emit(sort);
  }

  protected selectScope(scope: ChatSessionScope): void {
    if (this.controlsDisabled() || scope === this.scope()) {
      return;
    }
    this.scopeChanged.emit(scope);
  }

  private createMenuItem(
    labelKey: string,
    selected: boolean,
    command: () => void,
  ): MenuItem {
    return {
      label: this.transloco.translate(labelKey, {}, 'featureAiAssistant'),
      icon: selected ? 'ui-check' : undefined,
      command,
    };
  }

  protected isSessionPending(deliverableId: UUID): boolean {
    return (
      deliverableId === this.loadingSessionId() ||
      deliverableId === this.renamingSessionId() ||
      this.pendingDeletionSessionIds().includes(deliverableId)
    );
  }
}
